/* Firebase Auth - Google sign-in, d.light Workspace accounts only.
 *
 * Enabled only when VITE_FIREBASE_API_KEY is set. Without it (local mock, or the
 * IAP-gated exec demo) every function here is a no-op and the app behaves as before.
 *
 * signInWithPopup, never signInWithRedirect: a redirect cannot complete inside a
 * cross-origin iframe, and the portal is meant to be embeddable in the intranet Site.
 * `hd: '*'` limits Google's chooser to Workspace accounts (d.light has three domains);
 * the API is what actually enforces which domains are allowed (apps/api/src/auth/verifyToken.ts).
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth, GoogleAuthProvider, onAuthStateChanged, reauthenticateWithPopup, signInWithPopup, signOut,
  type Auth, type User,
} from 'firebase/auth'

const CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

export const authEnabled = (): boolean => !!CONFIG.apiKey

let app: FirebaseApp | null = null
let auth: Auth | null = null

function getFirebaseAuth(): Auth | null {
  if (!authEnabled()) return null
  if (!auth) {
    app = initializeApp(CONFIG)
    auth = getAuth(app)
  }
  return auth
}

export function watchUser(cb: (user: User | null) => void): () => void {
  const a = getFirebaseAuth()
  if (!a) { cb(null); return () => {} }
  return onAuthStateChanged(a, cb)
}

export async function signIn(): Promise<void> {
  const a = getFirebaseAuth()
  if (!a) return
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ hd: '*', prompt: 'select_account' })
  await signInWithPopup(a, provider)
}

export async function signOutUser(): Promise<void> {
  forgetGoogleGroupsToken()
  const a = getFirebaseAuth()
  if (a) await signOut(a)
}

/* My groups: a Google access token with the Cloud Identity Groups scope, asked for only
   when an owner opens their groups. Firebase re-authenticates the SAME account (a different
   account is refused) and hands back Google's token. Kept in memory for this tab only, never
   stored; Google tokens last an hour and Firebase does not renew them, so the page asks the
   owner to connect again when it expires. */
export const GROUPS_SCOPE = 'https://www.googleapis.com/auth/cloud-identity.groups'
let groupsToken: { token: string; expiresAt: number } | null = null

export function googleGroupsToken(): string | null {
  if (!authEnabled()) return 'mock-google-token'
  return groupsToken && groupsToken.expiresAt > Date.now() ? groupsToken.token : null
}

export function forgetGoogleGroupsToken(): void {
  groupsToken = null
}

/* Preferred path: Google Identity Services token client with the RMS account as the hint and
   no forced prompt. Google asks for the Groups permission once; after that a click returns a
   token from a popup that closes itself, with no account chooser or password. Needs the site's
   origin (…web.app) listed on the Firebase web OAuth client, so it is on only when
   VITE_GOOGLE_GROUPS_CLIENT_ID is set. Otherwise: Firebase re-authentication (account chooser). */
const GIS_SRC = 'https://accounts.google.com/gsi/client'

function loadGis(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as unknown as { google?: { accounts?: unknown } }).google?.accounts) { resolve(); return }
    const el = document.createElement('script')
    el.src = GIS_SRC
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('Could not reach Google. Check your connection and try again.'))
    document.head.appendChild(el)
  })
}

async function connectWithGis(clientId: string, email: string): Promise<void> {
  await loadGis()
  type TokenResponse = { access_token?: string; expires_in?: number; error?: string; scope?: string }
  type GisError = { type?: string; message?: string }
  const google = (window as unknown as {
    google: { accounts: { oauth2: { initTokenClient: (o: {
      client_id: string; scope: string; hint: string; prompt: string; include_granted_scopes: boolean
      callback: (r: TokenResponse) => void; error_callback: (e: GisError) => void
    }) => { requestAccessToken: () => void } } } }
  }).google
  await new Promise<void>((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: GROUPS_SCOPE,
      hint: email,
      prompt: '',
      include_granted_scopes: true,
      callback: (r) => {
        if (r.error || !r.access_token) { reject(new Error(r.error === 'access_denied' ? 'You did not allow RMS to manage your groups.' : 'Google did not connect. Try again.')); return }
        groupsToken = { token: r.access_token, expiresAt: Date.now() + Math.max(60, (r.expires_in ?? 3600) - 300) * 1000 }
        resolve()
      },
      error_callback: (e) => reject(new Error(e.type === 'popup_closed' ? 'The Google window was closed before connecting.' : e.message ?? 'Google did not connect. Try again.')),
    })
    client.requestAccessToken()
  })
}

export async function connectGoogleGroups(): Promise<void> {
  const a = getFirebaseAuth()
  if (!a?.currentUser) return
  const gisClientId = import.meta.env.VITE_GOOGLE_GROUPS_CLIENT_ID
  if (gisClientId) return connectWithGis(gisClientId, a.currentUser.email ?? '')
  const provider = new GoogleAuthProvider()
  provider.addScope(GROUPS_SCOPE)
  provider.setCustomParameters({ hd: '*', login_hint: a.currentUser.email ?? '' })
  const result = await reauthenticateWithPopup(a.currentUser, provider)
  const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken
  if (!token) throw new Error('Google did not return a connection. Try again.')
  groupsToken = { token, expiresAt: Date.now() + 55 * 60_000 }
}

/** Current user's Firebase ID token, refreshed by the SDK when near expiry. */
export async function idToken(): Promise<string | null> {
  const a = getFirebaseAuth()
  return a?.currentUser ? a.currentUser.getIdToken() : null
}
